import { supabase } from '../lib/supabase';

/**
 * Gets the chatbot public ID in any context
 * 
 * This utility function can be used in any part of the application
 * to get the current active chatbot's public ID. It first tries to get
 * the ID from a provided parameter, and if none is provided, it falls
 * back to getting the default chatbot for the logged in user.
 * 
 * @param chatbotPublicId Optional chatbotPublicId to use
 * @returns The chatbot public ID or undefined if none found
 */
export async function getChatbotPublicId(chatbotPublicId?: string): Promise<string | undefined> {
  // If a chatbotPublicId is provided, use it
  if (chatbotPublicId) {
    return chatbotPublicId;
  }
  
  try {
    // Get the current user
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    // Check for user error first
    if (userError) {
      return undefined;
    }

    // Validate user exists
    if (!user?.id) {
      return undefined;
    }

    // Get the default chatbot for this user
    const { data: chatbotData, error: chatbotError } = await supabase
      .from('chatbots')
      .select('public_id')
      .eq('user_id', user.id)
      .eq('is_default', true)
      .maybeSingle();

    if (chatbotError) {
      return undefined;
    }

    if (!chatbotData) {
      return undefined;
    }

    return chatbotData.public_id;
  } catch (err) {
    return undefined;
  }
}

export async function getChatbotPublicIdByUserId(userId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('chatbots')
      .select('public_id')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      return null;
    }

    return data?.public_id || null;
  } catch (error) {
    return null;
  }
}